// src/services/achievementSuggestionService.ts
import axios from 'axios';
import { API_CONFIG } from '../components/config/api';
import { Achievement, Category, Group } from './achievementService';
import { getStoredLanguage } from './languageService';
import { isAuthenticated, getAccessToken } from './authService';
import { translate } from './translationService';

/**
 * Interface for achievement suggestion categories
 */
export interface SuggestionCategory {
  id: string;
  name: string;
  achievements: Achievement[];
}

/**
 * Fetches personalized achievement suggestions from the server
 */
export const getAchievementSuggestions = async (): Promise<SuggestionCategory[]> => {
  try {
    const language = getStoredLanguage();
    let url = `${API_CONFIG.BASE_URL}/achievements/suggestions?lang=${language}`;

    // Add user parameter if authenticated
    const userIsAuthenticated = isAuthenticated();
    if (userIsAuthenticated) {
      url += '&user=true';
    }

    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${getAccessToken()}`
      }
    });

    if (response.data.status === 'success' && response.data.data && response.data.data.categories) {
      const categories = response.data.data.categories;
      
      // Translate category names if they aren't already localized by the backend
      const translatedCategories = categories.map((category: SuggestionCategory) => {
        // If category name is already localized by the backend, keep it
        if (category.name) {
          return category;
        }
        
        // Otherwise, use the translation service to localize the name
        const translatedName = getLocalizedCategoryName(category.id, language);
        return {
          ...category,
          name: translatedName
        };
      });

      // Deduplicate achievements in each category
      const deduplicatedCategories = translatedCategories.map((category: SuggestionCategory) => ({
        ...category,
        achievements: deduplicateAchievements(category.achievements)
      }));
      
      // Deduplicate across categories
      const result = deduplicateAchievementsAcrossCategories(deduplicatedCategories);
      
      return result;
    }
    
    // If the server doesn't return data, return an empty array
    return [];
  } catch (error) {
    console.error('Error fetching achievement suggestions:', error);
    console.log("Returning empty array due to error");
    return [];
  }
};

/**
 * Get the localized name for a category based on its ID
 */
export const getLocalizedCategoryName = (categoryId: string, language: string): string => {
  // Use the translation system to get localized category names
  const translationKey = `achievements.${categoryId}`;
  let localizedName;
  
  switch (categoryId) {
    // Removed almostComplete and inProgress cases
    case 'raid':
      localizedName = translate('achievements.raids', language, 'Raids');
      break;
    case 'dungeon':
      localizedName = translate('achievements.dungeons', language, 'Dungeons');
      break;
    case 'strike':
      localizedName = translate('achievements.strikes', language, 'Strikes');
      break;
    case 'crucible':
      localizedName = translate('achievements.crucible', language, 'Crucible');
      break;
    case 'gambit':
      localizedName = translate('achievements.gambit', language, 'Gambit');
      break;
    case 'world':
      localizedName = translate('achievements.destinations', language, 'Destinations');
      break;
    case 'seasonal':
      localizedName = translate('achievements.seasonal', language, 'Seasonal');
      break;
    case 'exotic':
      localizedName = translate('achievements.exotics', language, 'Exotics');
      break;
    case 'campaign':
      localizedName = translate('achievements.campaign', language, 'Campaign');
      break;
    case 'lostsector':
      localizedName = translate('achievements.lostsectors', language, 'Lost Sectors');
      break;
    case 'other':
      localizedName = translate('achievements.other', language, 'Other');
      break;
    default:
      // Fallback to a capitalized version of the ID if no translation exists
      localizedName = translate(translationKey, language, categoryId.charAt(0).toUpperCase() + categoryId.slice(1));
  }
  
  return localizedName;
};

/**
 * Deduplicates achievements in a list based on ID
 */
const deduplicateAchievements = (achievements: Achievement[]): Achievement[] => {
  if (!achievements || !Array.isArray(achievements)) {
    console.warn('deduplicateAchievements: Expected array but got', typeof achievements);
    return [];
  }
  
  const uniqueMap = new Map<number, Achievement>();
  
  achievements.forEach(achievement => {
    if (!uniqueMap.has(achievement.id)) {
      uniqueMap.set(achievement.id, achievement);
    }
  });
  
  return Array.from(uniqueMap.values());
};

/**
 * Deduplicates achievements across categories
 * Prioritizes achievements with higher progress and keeps them
 * only in the category with the highest priority
 */
const deduplicateAchievementsAcrossCategories = (categories: SuggestionCategory[]): SuggestionCategory[] => {
    
    // Define priority order: lower number = higher priority
    // Removed almostComplete and inProgress categories
    const categoryPriority: Record<string, number> = {
      'raid': 1,
      'dungeon': 2,
      'strike': 3,
      'crucible': 4,
      'gambit': 5,
      'world': 6,
      'lostsector': 7, // Neue Kategorie mit hoher Priorität
      'seasonal': 8,
      'exotic': 9,
      'campaign': 10,
      'other': 11
    };
    
    // Collect all achievements with their categories
    const achievementMap = new Map<number, { achievement: Achievement, categoryId: string, priority: number }>();
    
    categories.forEach(category => {
      const priority = categoryPriority[category.id] || 999; // High number for unknown categories
      
      if (!category.achievements || !Array.isArray(category.achievements)) {
        console.warn(`Category ${category.id} has no achievements array`);
        return;
      }
      
      category.achievements.forEach(achievement => {
        const existingEntry = achievementMap.get(achievement.id);
        
        if (!existingEntry || priority < existingEntry.priority) {
          // New entry or this has higher priority
          achievementMap.set(achievement.id, { achievement, categoryId: category.id, priority });
        }
      });
    });
    
    // Create new categories with deduplicated achievements
    const result: SuggestionCategory[] = categories.map(category => {
      // Find all achievements that should belong to this category
      const categoryAchievements = Array.from(achievementMap.values())
        .filter(entry => entry.categoryId === category.id)
        .map(entry => entry.achievement);
      
      return {
        ...category,
        achievements: categoryAchievements
      };
    });
    
    
    // Since we removed progress-based categories, we now include all categories
    const filteredResult = result;

    return filteredResult;
  };

/**
 * Creates achievement suggestions from grouped achievement data
 * Used as a client-side fallback when the server doesn't provide suggestions
 */
export const createSuggestionsFromGroups = (groups: Group[]): SuggestionCategory[] => {
    if (!groups || !Array.isArray(groups) || groups.length === 0) {
      return [];
    }
    
    const language = getStoredLanguage();
    
    // Predefined category IDs that match the backend
    const categoryMap: Record<number, string> = {
      1: 'raid',
      2: 'dungeon',
      3: 'strike',
      4: 'world',
      5: 'crucible',
      6: 'gambit',
      7: 'seasonal',
      8: 'exotic',
      9: 'campaign',
      10: 'other',
      11: 'lostsector'
    };
    
    // Create a map for suggestion categories with localized names
    // Removed inProgress and almostComplete categories
    const suggestionsMap: Record<string, SuggestionCategory> = {};
    
    // Add all category types to ensure consistency
    Object.entries(categoryMap).forEach(([typeId, categoryId]) => {
      suggestionsMap[categoryId] = { 
        id: categoryId, 
        name: getLocalizedCategoryName(categoryId, language),
        achievements: [] 
      };
    });
    
    // Collect all achievements from all groups
    // Removed progressBasedAchievements since we don't need those categories anymore
    const achievementsByCategoryType: Record<number, Achievement[]> = {};
    
    // Function to collect and categorize achievements
    const processAchievement = (achievement: Achievement) => {
      // Skip completed achievements
      if (achievement.is_completed) {
        return;
      }
      
      // Categorize by category_type_id
      const categoryTypeId = achievement.category_type_id;
      if (categoryTypeId && categoryMap[categoryTypeId]) {
        if (!achievementsByCategoryType[categoryTypeId]) {
          achievementsByCategoryType[categoryTypeId] = [];
        }
        achievementsByCategoryType[categoryTypeId].push(achievement);
      }
    };
    
    // Recursive function to process all achievements in the hierarchy
    const collectAchievements = (category: Category) => {
      if (category.achievements && Array.isArray(category.achievements)) {
        category.achievements.forEach(processAchievement);
      }
      
      if (category.subcategories && Array.isArray(category.subcategories)) {
        category.subcategories.forEach(subcategory => collectAchievements(subcategory));
      }
    };
    
    // Process all achievements
    groups.forEach(group => {
      if (group.categories && Array.isArray(group.categories)) {
        group.categories.forEach(category => collectAchievements(category));
      }
    });
    
    // Assign achievements to their categories
    // Removed progress-based categories assignments
    
    // Then the type-based categories
    Object.entries(achievementsByCategoryType).forEach(([typeId, achievements]) => {
      const categoryId = categoryMap[Number(typeId)];
      if (categoryId && suggestionsMap[categoryId]) {
        suggestionsMap[categoryId].achievements = achievements
          .sort((a, b) => (b.progress?.percentage || 0) - (a.progress?.percentage || 0))
          .slice(0, 5);
      }
    });
    
    // Convert to array - include all categories
    const resultArray: SuggestionCategory[] = [];
    
    // Add all type-based categories (even if empty)
    Object.entries(categoryMap).forEach(([typeId, categoryId]) => {
      resultArray.push(suggestionsMap[categoryId]);
    });
    
    return resultArray;
  };